import { DateTime } from "luxon"

export enum ResourceType {
    ROOM = "ROOM",
    MOBILE_AV = "MOBILE_AV",
    CAR = "CAR",
}

export enum IntegrationType {
    NONE = "NONE",
    MEETING_INVITE = "MEETING_INVITE",
    EWS = "EWS",
    STARLEAF = "STARLEAF",
}

export type OrganizationResource = Readonly<{
    id: number
    reference: string
    name: string
    status: string
    image: {
        url: string
    }
}>

export type OrganizationResourceRoom = Readonly<
    OrganizationResource & {
        type: ResourceType.ROOM
        buffer: number
        floor: {
            name: string
            value: number
        }
        seats: number
    }
>

export type OrganizationResourceMobileAV = Readonly<
    OrganizationResource & {
        type: ResourceType.MOBILE_AV
    }
>

export type OrganizationResourceCar = Readonly<
    OrganizationResource & {
        type: ResourceType.CAR
    }
>

export function isRoomResource(resource: OrganizationResourceUnion): resource is OrganizationResourceRoom {
    return resource.type === ResourceType.ROOM
}

export function isMobileAVResource(resource: OrganizationResourceUnion): resource is OrganizationResourceMobileAV {
    return resource.type === ResourceType.MOBILE_AV
}

export function isCarResource(resource: OrganizationResourceUnion): resource is OrganizationResourceCar {
    return resource.type === ResourceType.CAR
}

export type OrganizationResourceUnion =
    | OrganizationResourceRoom
    | OrganizationResourceMobileAV
    | OrganizationResourceCar

export enum WalkDirection {
    NORTH = "NORTH",
    EAST = "EAST",
    SOUTH = "SOUTH",
    WEST = "WEST",
}

export enum RoomDetailsColor {
    RED = "red",
    GREEN = "green",
    BLUE = "blue",
    BLACK = "black",
    LIGHT_BLUE = "lightBlue",
}

export enum ReminderType {
    MEETING_CONFIRMATION = "MEETING_CONFIRMATION",
    FIRST_REMINDER = "FIRST_REMINDER",
    MEETING_START = "MEETING_START",
    PREPARATION = "PREPARATION",
    CLEANUP = "CLEANUP",
    CUSTOM_REMINDER = "CUSTOM_REMINDER",
}

export type ReminderConfig = {
    label: string
    showDropdown: boolean
    dropdownOptions?: number[] // in hours or minutes, depending on use
    showTextField?: boolean
}

export type ResourceNotification = {
    id: number
    type: ReminderType
    receiverType: string
    messageType: string
    isActive: boolean
    message: string
    sendInMinutesBeforeMeeting?: number
    sendInMinutesAfterMeeting?: number
    emails?: string[]
}

export type ResourceNotificationForm = Readonly<
    Omit<ResourceNotification, "emails" | "isActive" | "receiverType" | "messageType">
>

type ClosingTimeForm = {
    closingTimesDaily?: {
        periodInTime: AvailabilityTimeRange<DateTime>
    }
    bookingTime?: Readonly<{
        periodInTime: AvailabilityTimeRange<DateTime>
    }>
    isClosingTimes?: boolean
    isBookingTime?: boolean
}

export enum BookingTimeType {
    AVAILABLE_ALL_DAY = "availableAllDay",
    ACCEPT_BOOKING_TIME = "acceptBookingTime",
    BLOCK_BOOKING_TIME = "blockBookingTime",
}

type BookingType = {
    bookingTimeType: BookingTimeType
}

export type CreateRoomResource = Readonly<{
    reference: string
    name: string
    isActive: boolean
    image: Readonly<{
        uid: string
    }>
    description: string
    email: string
    buffer?: number
    color: string
    roomDetails: Readonly<{
        sittings?: number
        floor?: number
        squareMeters?: number
        walkDirection?: WalkDirection
        tableSetups?: number[]
        isPrivateOffice: boolean
        avEquipments?: Array<{
            id: number
            name: string
        }>
    }>
    accessControl: Readonly<{
        limitAccess: boolean
        visibleForAll: boolean
        departmentIds: number[]
    }>
    integrations: {
        type: IntegrationType
        emailAddress: string
    }[]
    inService: boolean
    tags: Array<string>
    bookingTime?: Readonly<{
        periodInTime: AvailabilityTimeRange
    }>
    closingTimes: Array<ClosingTime>
    notifications: Array<ResourceNotification>
}>

type IntegrationFormType = {
    integrationType: IntegrationType
    integrationEmailAddress: string
}

export type CreateRoomResourceForm = Omit<CreateRoomResource, "bookingTime" | "integrations"> &
    ClosingTimeForm &
    IntegrationFormType &
    BookingType

export enum ClosingTimeType {
    PERIOD = "PERIOD",
    DAILY = "DAILY",
}

export type AvailabilityTimeRange<DateType = string> = {
    from: DateType
    to: DateType
}

export type ClosingTime = {
    type: ClosingTimeType
    reason?: string
    periodInTime?: AvailabilityTimeRange
    dateTimeInterval?: AvailabilityTimeRange
}

// Create Car

export type CreateCarResource = Readonly<{
    name: string
    isActive: boolean
    image: Readonly<{
        uid: string
    }>
    description: string
    accessControl: {
        limitAccess: boolean
        visibleForAll: boolean
        departmentIds: number[]
    }
    closingTimes: Array<ClosingTime>
    carDetails: {
        type: string
        status: string
        registrationNumber: string
        parkingLocation: string
        keysLocation: string
    }
    notifications: Array<ResourceNotification>
    tags: Array<string>
    bookingTime?: Readonly<{
        periodInTime: AvailabilityTimeRange
    }>
}>

export type CreateCarResourceForm = Omit<CreateCarResource, "bookingTime"> & ClosingTimeForm & BookingType

// Create Mobile AV

export type CreateMobileAVResource = Readonly<{
    name: string
    isActive: boolean
    image: Readonly<{
        uid: string
    }>
    description: string
    accessControl: {
        limitAccess: boolean
        visibleForAll: boolean
        departmentIds: number[]
    }
    buffer?: number
    color: string
    closingTimes: Array<ClosingTime>
    avDetails: {
        type: string
        serialNumber: string
        storageLocation: string
        floor: number
    }
    notifications: Array<ResourceNotification>
    tags: Array<string>
    bookingTime?: Readonly<{
        periodInTime: AvailabilityTimeRange
    }>
}>

export type CreateMobileAVResourceForm = Omit<CreateMobileAVResource, "bookingTime"> & ClosingTimeForm & BookingType
